/**
 * Simple HTTP Client
 * Clean, maintainable, and easy to understand
 */

import axios, { AxiosInstance, AxiosResponse } from "axios";
import { logger } from "../utils/logger";

export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  statusCode?: number;
  timestamp?: string;
}

export class HttpClient {
  private client: AxiosInstance;

  constructor(
    baseURL: string,
    options?: {
      timeout?: number;
    },
  ) {
    this.client = axios.create({
      baseURL,
      timeout: options?.timeout || 10000,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });

    this.setupInterceptors();
  }

  getBaseURL(): string {
    return this.client.defaults.baseURL || "";
  }

  private setupInterceptors() {
    // Request interceptor - handle FormData and logging
    this.client.interceptors.request.use(
      (config) => {
        logger.debug(`📤 HttpClient: Making request to: ${config.url}`, {
          method: config.method?.toUpperCase(),
          withCredentials: config.withCredentials,
          headers: config.headers,
        });

        // Handle FormData - remove Content-Type and disable timeout
        if (config.data instanceof FormData) {
          delete config.headers["Content-Type"];
          config.timeout = 0; // 0 = no timeout for uploads
          logger.debug(
            "📎 HttpClient: FormData detected — removed Content-Type, disabled timeout",
          );
        }

        return config;
      },
      (error) => {
        console.error("❌ HttpClient: Request interceptor error:", error);
        return Promise.reject(error);
      },
    );

    // Response interceptor - handle session expiration
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        logger.debug("📥 HttpClient: Response received:", {
          url: response.config.url,
          status: response.status,
          hasData: !!response.data,
        });
        return response;
      },
      async (error) => {
        const originalRequest = error.config;
        logger.debug("❌ HttpClient: Response error:", {
          url: originalRequest?.url,
          status: error.response?.status,
          message: error.message,
        });

        // Check for authentication errors (401)
        if (error.response?.status === 401) {
          logger.debug(
            "🔒 HttpClient: 401 error detected, checking error details...",
          );

          // Extract error details from response
          const errorData = error.response?.data?.error || error.response?.data;
          const errorCode = errorData?.code || errorData?.errorCode;
          const errorMessage = errorData?.message || "Session expired";

          logger.debug("🔍 HttpClient: Error details:", {
            code: errorCode,
            message: errorMessage,
          });

          // Check if this is a login or session check request that failed
          if (
            originalRequest.url?.includes("/auth/get-session") ||
            originalRequest.url?.includes("/auth/login")
          ) {
            logger.debug("🔄 HttpClient: Check session or login request failed");
            // Silent failure for session checks - AuthContext handles the state
            return Promise.reject(error);
          }

          // Handle specific session expiration errors
          if (
            errorCode === "SESSION_EXPIRED" ||
            errorMessage.toLowerCase().includes("session expired")
          ) {
            logger.debug("⏰ HttpClient: Session expired error detected");
            this.handleSessionExpiration("session_expired", errorMessage);
            return Promise.reject(error);
          }

          // Handle device conflict errors (for students)
          if (
            errorCode === "DEVICE_CONFLICT" ||
            errorMessage.toLowerCase().includes("device conflict")
          ) {
            logger.debug("📱 HttpClient: Device conflict error detected");
            this.handleSessionExpiration("device_conflict", errorMessage);
            return Promise.reject(error);
          }

          // Handle invalid session errors
          if (
            errorCode === "SESSION_INVALID" ||
            errorCode === "INVALID_TOKEN" ||
            errorMessage.toLowerCase().includes("session invalid") ||
            errorMessage.toLowerCase().includes("invalid token")
          ) {
            logger.debug("🚫 HttpClient: Invalid session error detected");
            this.handleSessionExpiration("session_invalid", errorMessage);
            return Promise.reject(error);
          }

          // Default session expiration handling for all other 401s
          this.handleSessionExpiration("unauthorized", errorMessage);
        }

        // Handle access forbidden
        if (error.response?.status === 403) {
          const errorData = error.response?.data?.error || error.response?.data;
          const errorCode = errorData?.code;
          const errorMessage = errorData?.message || "Access denied";

          logger.debug("🚫 HttpClient: 403 error detected", {
            code: errorCode,
            message: errorMessage,
          });

          // Handle banned account
          if (errorCode === "ACCOUNT_BANNED") {
            logger.debug("🚫 HttpClient: Account banned error detected");
            this.handleSessionExpiration("account_banned", errorMessage);
            return Promise.reject(error);
          }

          // Handle privacy policy update required
          if (errorCode === "PRIVACY_POLICY_UPDATE_REQUIRED") {
            logger.debug("📋 HttpClient: Privacy policy update required");
            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      },
    );
  }

  private handleSessionExpiration(reason: string, message?: string) {
    logger.debug(
      `🚨 HttpClient: Handling session expiration - reason: ${reason}`,
    );

    // Determine user-friendly error message based on reason
    let userMessage = "Your session has expired. Please log in again.";

    switch (reason) {
      case "session_expired":
        userMessage =
          message || "Your session has expired. Please log in again.";
        break;
      case "device_conflict":
        userMessage =
          message ||
          "You have been logged in from another device. Please log in again.";
        break;
      case "session_invalid":
        userMessage =
          message || "Your session is invalid. Please log in again.";
        break;
      case "account_banned":
        userMessage =
          message || "Your account has been banned. Please contact support.";
        break;
      default:
        userMessage = message || "Authentication failed. Please log in again.";
    }

    // AuthProvider re-reads the session on this event; the route guards then
    // navigate to login without reloading the page.
    window.dispatchEvent(
      new CustomEvent("auth:sessionExpired", {
        detail: {
          reason,
          message: userMessage,
        },
      }),
    );

    // Store error message for display on login page
    try {
      sessionStorage.setItem("auth:errorMessage", userMessage);
      sessionStorage.setItem("auth:errorReason", reason);
    } catch (e) {
      console.warn("⚠️ HttpClient: Could not store error message:", e);
    }
  }

  async get<T = any>(url: string, params?: any): Promise<T> {
    const response = await this.client.get<ApiResponse<T>>(url, { params });
    return (response.data.data || response.data) as T;
  }

  async getResponse<T = any>(
    url: string,
    params?: any,
  ): Promise<AxiosResponse<ApiResponse<T>>> {
    return this.client.get<ApiResponse<T>>(url, { params });
  }

  async post<T = any>(
    url: string,
    data?: any,
    options?: { timeout?: number },
  ): Promise<T> {
    const response = await this.client.post<ApiResponse<T>>(url, data, {
      timeout: options?.timeout,
    });
    return (response.data.data || response.data) as T;
  }

  async put<T = any>(url: string, data?: any): Promise<T> {
    const response = await this.client.put<ApiResponse<T>>(url, data);
    return (response.data.data || response.data) as T;
  }

  async patch<T = any>(url: string, data?: any): Promise<T> {
    const response = await this.client.patch<ApiResponse<T>>(url, data);
    return (response.data.data || response.data) as T;
  }

  async delete<T = any>(url: string): Promise<T> {
    const response = await this.client.delete<ApiResponse<T>>(url);
    return (response.data.data || response.data) as T;
  }

  // For file uploads or custom requests
  async request<T = any>(config: any): Promise<T> {
    const response = await this.client.request<ApiResponse<T>>(config);
    return (response.data.data || response.data) as T;
  }
}
